/*
 * NAV LINK DEFINITIONS
 *
 * Evaluated by  src/shared/utilities/linkAccessFilter.function.js
 * Rendered by   src/components/Admin/AgentNavLinks.component.jsx
 *
 * Exports MAJOR_NAV_LINKS (array) and MINOR_NAV_LINKS (object of arrays).
 *
 * ---------------------------------------------------------------------------
 * SHAPE
 * ---------------------------------------------------------------------------
 * url        Route, or an absolute http(s) URL.  Two placeholders are
 *            substituted at render time (AgentNavLinks.getUrl):
 *              :userId       -> the user's u_login
 *              :recruiterId  -> the user's id, AES-encrypted
 * label      Display text.  NOT purely cosmetic -- linkAccessFilter keys
 *            several special cases off the exact label string (e.g. 'Agent
 *            Enrollment', 'Billing & Benefits').  Renaming a label can change
 *            who may access it.
 * icon       MDB icon name.  https://mdbootstrap.com/docs/b4/react/content/icons-list/
 * onClick    Named handler instead of navigation (e.g. 'enrollmentHandler').
 * children   Nested link definitions (renders as a dropdown).
 * urlRegex   Present on many entries but currently read by no consumer.
 *
 * ---------------------------------------------------------------------------
 * ACCESS  -- evaluated in this order by canAccessLink()
 * ---------------------------------------------------------------------------
 * 1. blocks     Deny rules, checked FIRST.
 *      .usertypes  Array of slugs.  ANY match -> denied.
 *      .profile    Array of user properties.  Denied only when EVERY listed
 *                  property is true or '1'.
 * 2. requires   Grant rules.
 *      .usertypes  ANY match passes.
 *      .profile    EVERY listed property must be true or '1'.
 *      .env        Link exists only in the listed environments
 *                  (e.g. ['local','beta']) -- used to hide WIP from prod.
 * 3. label-based special cases (see linkAccessFilter) -- these RETURN and
 *    override the result above for certain usertypes.
 *
 * overrides   Consulted only while logged in AS a downline agent, and only
 *             when canAccessLink() is called with inclOverrides.  Lets the
 *             parent user reach a link the child account cannot.
 *               .usertypes / .profile, evaluated against the PARENT user.
 *
 * readonly    .usertypes -- ANY match renders the link in a readonly state
 *             (canReadonlyLink).  Independent of access.
 *
 * Failing access does not remove a link; it renders with `is-hidden-link`.
 *
 * ---------------------------------------------------------------------------
 * locked  vs  disabled
 * ---------------------------------------------------------------------------
 * locked      The link RENDERS and remains visible, but clicking it does not
 *             navigate -- the click is routed to NavigationStore.onLockedClick,
 *             which is expected to tell the user why and how to resolve it.
 *             Accepts:
 *               true
 *               () => boolean
 *               'HAS_OUTSTANDING_LEADER_REQUIREMENTS'  (named rule resolved in
 *                 isLinkLocked; see doRequirementsBlockEnrollment)
 *             Re-evaluated at click time, not just at render.
 * disabled    true | () => boolean.  The link is filtered out entirely and
 *             never rendered.
 *
 * ---------------------------------------------------------------------------
 * MULTIPLE DEFINITIONS FOR ONE BUTTON
 * ---------------------------------------------------------------------------
 * Each definition is evaluated independently, so two definitions sharing a
 * label/onClick act as separate GRANT PATHS, OR'd together -- if either
 * passes, the button is reachable.  See the two 'Agent Enrollment' entries:
 * one grants by usertype, the other by the per-user `can_recruit` override for
 * users whose usertype is not on the allowlist.
 *
 * Two consequences:
 *   - Policy such as `locked` must be repeated on EVERY path.  It belongs to
 *     the button, but is declared per definition, and nothing enforces that.
 *   - `blocks` is sometimes used to keep two paths mutually exclusive so the
 *     button does not render twice.  That is de-duplication, NOT policy.  The
 *     `blocks.profile: ['can_recruit']` on the first Agent Enrollment entry
 *     does not mean "recruiters are blocked from enrolling".
 */

const MAJOR_NAV_LINKS = [
  {
    label: 'Agent Resources',
    requires: {},
    blocks: {},
    overrides: {},
    children: [
      {
        // UAD-3667: only shown to white-label-enabled users (own is_white_label flag).
        url: '/agency-branding',
        label: 'Agency Branding',
        requires: {
          profile: ['is_white_label'],
        },
      },
      {
        url: '/first-sale-tracker',
        label: 'First Sale Tracker',
        requires: {},
        blocks: {
          usertypes: ['affiliate-group', 'associate-group', 'limited-to-contracting-signature-agent'],
        },
      },
      {
        url: '/tools',
        label: 'Agent Tools',
        requires: {},
        blocks: {
          usertypes: ['limited-to-contracting-signature-agent'],
        },
        overrides: {
          usertypes: ['system-admin', 'internal-admin'],
        },
      },
      {
        label: 'Agent Website',
        icon: 'globe-americas',
        children: [
          {
            url: `/:userId`,
            label: 'My Website',
            requires: {
              usertypes: ['agent', 'system-admin', 'internal-admin'],
            },
            blocks: {
              usertypes: ['affiliate-group', 'associate-group', 'limited-to-contracting-signature-agent'],
            },
          },
          {
            url: '/website-info',
            label: 'Website Info',
            requires: {
              usertypes: ['agent', 'system-admin'],
            },
            blocks: {
              usertypes: ['affiliate-group', 'associate-group', 'limited-to-contracting-signature-agent'],
            },
          },
          {
            url: '/website-branding',
            label: 'Website Branding',
            requires: {
              usertypes: ['agent', 'system-admin'],
            },
            blocks: {
              usertypes: ['affiliate-group', 'associate-group', 'limited-to-contracting-signature-agent'],
              profile: ['show_white_label'],
            },
          },
          {
            url: '/menu-links',
            label: 'Menu & Links',
            requires: {
              usertypes: ['agent', 'system-admin'],
            },
            blocks: {
              usertypes: ['affiliate-group', 'associate-group', 'limited-to-contracting-signature-agent'],
            },
          },
          {
            url: '/maintain-products',
            label: 'Carrier Links',
            requires: {
              usertypes: ['agent', 'system-admin'],
            },
            blocks: {
              usertypes: ['affiliate-group', 'associate-group', 'limited-to-contracting-signature-agent'],
            },
          },
          {
            url: '/website-contact-requests',
            label: 'Contact Requests',
            requires: {
              usertypes: ['agent', 'system-admin'],
            },
            blocks: {
              usertypes: ['affiliate-group', 'associate-group', 'limited-to-contracting-signature-agent'],
            },
          },
          {
            url: '/website-quote-requests',
            label: 'Quote Requests',
            requires: {
              usertypes: ['agent', 'system-admin'],
            },
            blocks: {
              usertypes: ['affiliate-group', 'associate-group', 'limited-to-contracting-signature-agent'],
            },
          },
        ],
      },
      {
        url: '/division-messages',
        label: 'Division Messages',
      },
      {
        url: '/eo-policy',
        label: 'E&O Policy',
        requires: {},
      },
      {
        url: '/email-signature',
        label: 'E-mail Signature',
        requires: {},
        blocks: {
          usertypes: ['affiliate-group', 'associate-group'],
        },
      },
      {
        url: `/kpi-goals`,
        label: 'KPI Goals',
        requires: {
          profile: ['has_onboarded'],
        },
        blocks: {
          usertypes: ['affiliate-group', 'associate-group', 'limited-to-contracting-signature-agent'],
        },
      },
      {
        url: '/agent-resources',
        label: 'Library',
        requires: {},
        blocks: {
          usertypes: ['limited-to-contracting-signature-agent'],
        },
      },
      {
        label: 'My Account',
        icon: 'user',
        children: [
          {
            url: '/account-profile',
            urlRegex: /\/account-profile/g,
            label: 'Profile',
            requires: {
              usertypes: ['agent', 'non-agent'],
            },
          },
          {
            url: '/account-password',
            urlRegex: '/account-password',
            label: 'Password',
            requires: {
              usertypes: ['agent', 'non-agent'],
            },
          },
          {
            url: '/account-billinginfo',
            urlRegex: '/account-billinginfo',
            label: 'Billing & Benefits',
            requires: {
              usertypes: ['agent', 'non-agent'],
            },
          },
          {
            url: '/account-terms',
            label: 'Terms',
            requires: {
              usertypes: ['agent', 'non-agent'],
            },
          },
          {
            url: '/account-alerts',
            label: 'Alerts',
            requires: {
              usertypes: ['agent', 'non-agent'],
            },
          },
          {
            url: '/beast-mode-setup',
            label: 'Beast Mode 10x CRM',
            requires: {
              env: ['local'],
              usertypes: ['system-admin'],
            },
          },
          {
            url: '/accounts/history',
            label: 'User History',
            requires: {
              usertypes: ['system-admin', 'agency-owner', 'internal-admin'],
            },
            overrides: {
              usertypes: ['system-admin', 'internal-admin'],
            },
          },
        ],
      },
      {
        label: 'My Wallet',
        url: '/account-ledger',
        requires: {
          profile: ['has_onboarded'],
        },
        blocks: {
          usertypes: ['affiliate-group', 'associate-group', 'limited-to-contracting-signature-agent'],
        },
        overrides: {
          usertypes: [
            'system-admin',
            'internal-admin',
            'internal-staff',
            'agency-owner',
            'divisional-leader',
            'regional-leader',
            'district-leader',
          ],
        },
      },
      {
        url: '/signature-agent-benefits',
        label: 'Signature Agent Benefits',
      },
      {
        url: '/state-licenses',
        label: 'State Licenses',
        requires: {},
      },
      {
        url: '/my-docs',
        label: 'My Docs',
        requires: {},
      },
      {
        url: 'https://10x.usabg.com/',
        label: 'Beast Mode 10x CRM Login',
        requires: {},
      },
    ],
  },
  {
    label: 'Submit Sales',
    url: '/submit-sales',
    requires: {},
    blocks: {},
  },
  {
    label: 'Swag Store',
    url: 'https://swag.usabg.com/',
    requires: {},
    blocks: {
      usertypes: ['affiliate-group', 'limited-to-contracting-signature-agent'],
    },
  },
  {
    label: 'Leader Boards',
    url: '/leaderboards',
    requires: {},
    blocks: {},
  },
  {
    label: 'REFERRAL PROGRAM',
    url: '/referral-program',
    requires: {},
    blocks: {},
  },
  {
    label: 'Get Leads',
    requires: {},
    blocks: {
      usertypes: ['affiliate-group', 'limited-to-contracting-signature-agent'],
    },
    overrides: {
      usertypes: ['system-admin', 'internal-admin'],
    },
    children: [
      {
        label: 'Lead Credit Programs',
        url: '/lead-programs',
        requires: {},
        blocks: {
          usertypes: ['limited-to-contracting-signature-agent', 'career-agent'],
        },
        overrides: {
          usertypes: ['system-admin', 'internal-admin'],
        },
      },
      {
        label: 'Order Leads',
        url: '/order-leads',
        requires: {},
        blocks: {
          usertypes: ['limited-to-contracting-signature-agent', 'career-agent'],
        },
        overrides: {
          usertypes: ['system-admin', 'internal-admin'],
        },
      },
      {
        label: 'My USABG BUCK$',
        url: '/account-ledger',
        requires: {
          profile: ['has_onboarded'],
        },
        blocks: {
          usertypes: ['affiliate-group', 'associate-group', 'limited-to-contracting-signature-agent'],
        },
        overrides: {
          usertypes: [
            'system-admin',
            'internal-admin',
            'internal-staff',
            'agency-owner',
            'divisional-leader',
            'regional-leader',
            'district-leader',
          ],
        },
      },
    ],
  },
  {
    label: 'Agent Training & Calendar',
    requires: {},
    children: [
      {
        url: 'https://firebasestorage.googleapis.com/v0/b/totemic-bonus-239316.appspot.com/o/agency_document%2Fagency_document.e9a7f723-9482-4c69-a57f-0adb445e94df.pdf?alt=media',
        label: 'Insurance Education',
        requires: {},
        blocks: {
          usertypes: ['affiliate-group', 'associate-agent'],
        },
        overrides: {
          usertypes: ['system-admin', 'internal-admin'],
        },
      },
      {
        url: '/trainings',
        label: 'Recorded Trainings',
        requires: {},
        blocks: {
          usertypes: ['limited-to-contracting-signature-agent'],
        },
        overrides: {
          usertypes: ['system-admin', 'internal-admin'],
        },
      },
      {
        label: 'Training Calendar',
        url: '/training-calendar',
        requires: {},
        blocks: {
          usertypes: ['limited-to-contracting-signature-agent'],
        },
        overrides: {
          usertypes: ['system-admin', 'internal-admin'],
        },
      },
      {
        label: 'USABG Events',
        url: '/events',
        requires: {},
        blocks: {
          usertypes: ['limited-to-contracting-signature-agent'],
        },
        overrides: {
          usertypes: ['system-admin', 'internal-admin'],
        },
      },
      {
        url: '/holidays',
        label: 'USABG Holidays',
        requires: {},
        blocks: {},
        overrides: {},
      },
      {
        label: 'USABG University',
        url: '/usabg-university',
        requires: {},
        blocks: {
          usertypes: ['affiliate-group', 'associate-group', 'limited-to-contracting-signature-agent'],
        },
      },
    ],
  },
  {
    label: 'Carrier Contracting & Commissions',
    url: '/agent-contracting',
    requires: {
      profile: ['has_onboarded'],
    },
    overrides: {
      profile: ['has_onboarded'],
    },
  },
  {
    label: 'Contests',
    requires: {},
    blocks: {},
    children: [
      {
        label: 'Carrier Contests',
        url: '/carrier-contests',
        requires: {},
        blocks: {},
      },
      {
        label: 'USABG Hall of Fame',
        url: '/hall-of-fame',
        requires: {},
        blocks: {},
      },
      {
        label: 'USABG Royal Ring',
        url: '/race-to-the-ring',
        requires: {},
        blocks: {},
      },
      {
        label: 'USABG BUCK$ Program',
        url: '/usabg-bucks-program',
        requires: {},
        blocks: {},
      },
    ],
  },
  {
    label: 'Agency Builder',
    icon: 'tasks',
    children: [
      {
        url: '/manage-agents',
        label: 'Manage My Agents',
        requires: {
          usertypes: [
            'system-admin',
            'agency-owner',
            'recruiter-group',
            'internal-admin',
            'division-admin',
            'region-admin',
            'district-admin',
            'signature-agent-complete',
          ],
        },
      },
      {
        url: '#',
        onClick: 'enrollmentHandler',
        label: 'Agent Enrollment',
        requires: {
          usertypes: ['system-admin', 'agency-owner', 'recruiter-group', 'signature-agent-complete'],
        },
        locked: 'HAS_OUTSTANDING_LEADER_REQUIREMENTS',
        blocks: {
          profile: ['can_recruit'],
        },
      },
      {
        url: '#',
        onClick: 'enrollmentHandler',
        label: 'Agent Enrollment',
        requires: {
          profile: ['can_recruit'],
        },
        locked: 'HAS_OUTSTANDING_LEADER_REQUIREMENTS',
      },
      {
        url: '/carrier-priorities',
        label: 'Carrier Priorities',
        requires: {
          usertypes: [
            'system-admin',
            'agency-owner',
            'recruiter-group',
            'internal-admin',
            'division-admin',
            'region-admin',
            'district-admin',
            'internal-admin',
            'internal-staff',
            'signature-agent-complete',
          ],
        },
      },
      // {
      //   url: '/create-career-agent',
      //   label: 'Create Career/BA Agent',
      //   requires: {
      //     usertypes: [
      //       'system-admin',
      //       'agency-owner',
      //       'divisional-leader',
      //       'internal-admin',
      //       'division-admin',
      //     ],
      //   },
      // },
      // {
      //   url: '/create-division-admin',
      //   label: 'Create Division Admin',
      //   requires: {
      //     usertypes: [
      //       'system-admin',
      //       'agency-owner',
      //       'divisional-leader',
      //       'internal-admin',
      //       'division-admin',
      //     ],
      //   },
      // },
      // {
      //   url: '/create-region-admin',
      //   label: 'Create Region Admin',
      //   requires: {
      //     usertypes: [
      //       'system-admin',
      //       'agency-owner',
      //       'regional-leader',
      //       'internal-admin',
      //       'region-admin',
      //     ],
      //   },
      // },
      // {
      //   url: '/create-district-admin',
      //   label: 'Create District Admin',
      //   requires: {
      //     usertypes: [
      //       'system-admin',
      //       'agency-owner',
      //       'district-leader',
      //       'internal-admin',
      //       'district-admin',
      //     ],
      //   },
      // },
      {
        url: '/create-admin-or-career-agent',
        label: 'Create Admin/Career Agents',
        requires: {
          usertypes: [
            'agency-owner',
            'internal-admin',
            'internal-stuff',
            'system-admin',
            'divisional-leader',
            'division-admin',
            'regional-leader',
            'region-admin',
            'district-leader',
            'district-admin',
            'signature-agent-complete',
          ],
        },
      },
      {
        url: '/create-admin-or-career-agent',
        label: 'Create Career Agent',
        requires: {
          usertypes: ['premier-agent'],
        },
      },
      {
        url: '/email-distribution',
        label: 'Email Distribution Lists',
        block: {
          usertypes: ['affiliate-group', 'associate-agent'],
        },
        requires: {
          usertypes: [
            'system-admin',
            'agency-owner',
            'recruiter-group',
            'internal-admin',
            'division-admin',
            'region-admin',
            'district-admin',
            'signature-agent-complete',
          ],
        },
      },
      // {
      //   url: '/welcome',
      //   label: 'Getting Started',
      //   requires: {
      //     usertypes: [
      //       'system-admin',
      //       'agency-owner',
      //       'recruiter-group',
      //       'internal-admin',
      //       'division-admin',
      //       'region-admin',
      //       'district-admin',
      //     ],
      //   },
      // },
      {
        url: '/leader-guidelines',
        label: 'Guidelines',
        requires: {
          usertypes: [
            'system-admin',
            'agency-owner',
            'recruiter-group',
            'internal-admin',
            'division-admin',
            'region-admin',
            'district-admin',
          ],
        },
      },
      {
        url: '/leader-resources',
        label: 'Leader Resources',
        requires: {
          usertypes: [
            'system-admin',
            'agency-owner',
            'recruiter-group',
            'internal-admin',
            'division-admin',
            'region-admin',
            'district-admin',
          ],
          block: ['affiliate-leader', 'associate-leader'],
        },
      },
      {
        url: '/team-growth-report',
        label: 'Team Growth Report',
        requires: {
          usertypes: [
            'system-admin',
            'agency-owner',
            'recruiter-group',
            'internal-admin',
            'division-admin',
            'region-admin',
            'district-admin',
            'signature-agent-complete',
          ],
        },
      },
      {
        url: '/prospects',
        label: 'My Prospects',
        requires: {
          usertypes: [
            'system-admin',
            'agency-owner',
            'recruiter-group',
            'division-admin',
            'region-admin',
            'district-admin',
            'signature-agent-complete',
          ],
        },
      },
    ],
  },
  {
    label: 'Reports',
    icon: 'chart-bar',
    children: [
      {
        url: '/manage-agents',
        label: 'Manage My Agents',
        requires: {
          usertypes: [
            'system-admin',
            'agency-owner',
            'recruiter-group',
            'internal-admin',
            'division-admin',
            'region-admin',
            'district-admin',
            'signature-agent-complete',
          ],
        },
      },
      {
        url: '/kpi-tracking',
        label: 'KPI Tracking',
        requires: {
          profile: ['has_onboarded'],
        },
        blocks: {
          usertypes: ['limited-to-contracting-signature-agent'],
        },
      },
      {
        url: '/kpi-dashboard',
        label: 'KPI Dashboard',
        requires: {},
        blocks: {
          usertypes: ['affiliate-group', 'associate-group', 'limited-to-contracting-signature-agent'],
        },
      },
      {
        url: '/reporting/my-wallet',
        label: 'Agent Wallet',
        requires: {},
        blocks: {
          usertypes: ['limited-to-contracting-signature-agent'],
        },
      },
      {
        url: '/reporting/kpi-report/kpi-performance',
        label: 'KPI Performance',
        requires: {},
        blocks: {
          usertypes: ['affiliate-group', 'associate-group', 'limited-to-contracting-signature-agent'],
        },
      },
      // {
      //   url: '/leaderboards',
      //   label: 'Leader Boards',
      //   requires: {},
      // },
      // {
      //   url: '/reporting/agent-ledgers',
      //   label: 'USABG BUCK$',
      //   requires: {},
      //   blocks: {
      //     usertypes: ['limited-to-contracting-signature-agent'],
      //   },
      // },
      {
        url: '/reporting/my-production',
        label: 'Production Reports',
        requires: {},
        blocks: {
          usertypes: ['limited-to-contracting-signature-agent'],
        },
      },
      // {
      //   url: '/reporting/downline-production',
      //   label: 'Downline Production',
      //   requires: {
      //     profile: ['has_onboarded'],
      //   },
      //   blocks: {
      //     usertypes: ['affiliate-agent', 'associate-agent'],
      //   },
      // },
      // {
      //   url: '/reporting/division-production',
      //   label: 'Division Production',
      //   requires: {
      //     profile: ['has_onboarded'],
      //     usertypes: [
      //       'agency-owner',
      //       'divisional-leader',
      //       'internal-admin',
      //       'system-admin',
      //       'division-admin',
      //     ],
      //   },
      // },
      // {
      //   url: '/reporting/agency-production',
      //   label: 'Agency Production',
      //   requires: {
      //     profile: ['has_onboarded'],
      //     usertypes: [
      //       'agency-owner',
      //       'divisional-leader',
      //       'internal-admin',
      //       'system-admin',
      //     ],
      //   },
      // },
      {
        url: '/reporting/agency-wallets',
        label: 'Agency Wallets',
        requires: {
          profile: ['has_onboarded'],
          usertypes: ['agency-owner', 'internal-admin', 'system-admin'],
        },
      },
      // {
      //   url: '/reporting/district-totals',
      //   label: 'District Totals',
      //   requires: {
      //     profile: ['has_onboarded'],
      //     usertypes: [
      //       'agency-owner',
      //       'system-admin',
      //       'internal-admin',
      //       'internal-staff',
      //       'district-leader',
      //       'regional-leader',
      //       'divisional-leader',
      //       'division-admin',
      //       'region-admin',
      //       'district-admin'
      //     ],
      //   },
      //   blocks: {
      //     usertypes: ['affiliate-group', 'associate-group'],
      //   },
      // },
      // {
      //   url: '/reporting/region-totals',
      //   label: 'Region Totals',
      //   requires: {
      //     profile: ['has_onboarded'],
      //     usertypes: [
      //       'agency-owner',
      //       'system-admin',
      //       'internal-admin',
      //       'internal-staff',
      //       'regional-leader',
      //       'divisional-leader',
      //       'division-admin',
      //       'region-admin',
      //     ],
      //   },
      //   blocks: {
      //     usertypes: ['affiliate-group', 'associate-group'],
      //   },
      // },
      // {
      //   url: '/reporting/division-totals',
      //   label: 'Division Totals',
      //   requires: {
      //     profile: ['has_onboarded'],
      //     usertypes: [
      //       'agency-owner',
      //       'system-admin',
      //       'internal-admin',
      //       'internal-staff',
      //       'divisional-leader',
      //       'division-admin',
      //     ],
      //   },
      //   blocks: {
      //     usertypes: ['affiliate-group', 'associate-group'],
      //   },
      // },
      // {
      //   url: '/reporting/leaders',
      //   label: 'Leader Reports',
      //   requires: {
      //     profile: ['has_onboarded'],
      //     usertypes: [
      //       'system-admin',
      //       'agency-owner',
      //       'recruiter-group',
      //       'division-admin',
      //       'region-admin',
      //       'district-admin',
      //       'premier-agent',
      //     ],
      //   },
      //   overrides: {
      //     usertypes: ['system-admin', 'internal-admin', 'divisional-leader'],
      //   },
      //   blocks: {
      //     usertypes: ['affiliate-group', 'associate-group'],
      //   },
      // },
      // {
      //   url: '/reporting/referrals',
      //   label: 'Referrals',
      //   requires: {
      //     usertypes: ['affiliate-group', 'associate-group'],
      //     profile: ['has_onboarded'],
      //   },
      // },
      {
        url: '/reporting/referrals',
        label: 'Referrals',
        requires: {
          profile: ['has_onboarded'],
        },
        blocks: {
          usertypes: ['affiliate-group', 'associate-group'],
        },
      },
      {
        url: '/reporting/internal',
        label: 'NSM Report',
        requires: {
          usertypes: ['system-admin', 'internal-admin', 'internal-staff', 'agency-owner'],
        },
      },
      {
        url: '/reports/growth-report',
        label: 'Personal Growth Report',
        requires: {
          profile: ['has_onboarded'],
        },
        blocks: {
          usertypes: ['affiliate-group', 'associate-group'],
        },
      },
      {
        url: '/reports/carrier-growth-report',
        label: 'Carrier Growth Report',
        requires: {
          profile: ['has_onboarded'],
          usertypes: [
            'agency-owner',
            'divisional-leader',
            'regional-leader',
            'district-leader',
            'internal-admin',
            'system-admin',
            'division-admin',
            'region-admin',
            'district-admin',
          ],
        },
        blocks: {
          usertypes: ['affiliate-group', 'associate-group'],
        },
      },
      {
        url: '/reports/leaders',
        label: 'Leader Report Card',
        requires: {
          profile: ['has_onboarded'],
          usertypes: [
            'internal-admin',
            'system-admin',
            'district-leader',
            'regional-leader',
            'divisional-leader',
            'agency-owner',
            'division-admin',
            'region-admin',
            'district-admin',
          ],
        },
        blocks: {
          usertypes: ['affiliate-group', 'associate-group'],
        },
      },
      {
        url: '/reports/subscriptions',
        label: 'Subscription Reports',
        requires: {
          usertypes: ['internal-admin', 'system-admin', 'agency-owner'],
        },
      },
      {
        url: '/team-growth-report',
        label: 'Team Growth Report',
        requires: {
          usertypes: [
            'system-admin',
            'agency-owner',
            'recruiter-group',
            'internal-admin',
            'division-admin',
            'region-admin',
            'district-admin',
          ],
        },
      },
    ],
  },
]

const MINOR_NAV_LINKS = {
  admin_actions: {
    label: 'Admin Actions',
    icon: 'lock',
    children: [
      {
        label: 'Agency Info',
        url: '/admin-info',
        urlRegex: '/admin-info',
        requires: {
          usertypes: ['agency-owner', 'system-admin', 'internal-admin', 'internal-staff'],
        },
      },
      {
        label: 'Agent Wallets',
        url: '/admin/ledgers',
        urlRegex: /\/admin\/ledgers/,
        requires: {
          usertypes: ['agency-owner', 'system-admin', 'internal-admin', 'internal-staff'],
        },
      },
      {
        label: 'Elite Agency Teams',
        url: '/elite-agency-teams',
        requires: {
          usertypes: ['agency-owner', 'system-admin', 'internal-admin', 'internal-staff'],
        },
      },
      {
        label: 'Calendars',
        url: '/admin-calendar',
        urlRegex: /\/admin-calendar/,
        requires: {
          usertypes: ['agency-owner', 'system-admin', 'internal-admin', 'internal-staff'],
        },
      },
      {
        label: 'Carriers',
        url: '/admin-carriers',
        urlRegex: /\/admin-carriers/,
        requires: {
          usertypes: ['agency-owner', 'system-admin', 'internal-admin', 'internal-staff'],
        },
      },
      {
        label: 'Commission Levels',
        url: '/admin-commissions',
        urlRegex: '/admin-commissions',
        requires: {
          usertypes: ['agency-owner', 'system-admin', 'internal-admin', 'internal-staff'],
        },
      },
      {
        label: 'Commission Payments',
        url: '/commission-payments',
        urlRegex: '/commission-payments',
        requires: {
          usertypes: ['system-admin', 'internal-admin', 'internal-staff'],
        },
      },
      {
        label: 'Contact Requests',
        url: '/contact-requests',
        urlRegex: '/contact-requests',
        requires: {
          usertypes: ['agency-owner', 'system-admin', 'internal-admin', 'internal-staff'],
        },
      },
      {
        label: 'Content Pages',
        url: '/content-pages',
        urlRegex: '/content-pages',
        requires: {
          usertypes: ['agency-owner', 'system-admin', 'internal-admin', 'internal-staff'],
        },
      },
      {
        label: 'Website Content',
        url: '/admin-website-content',
        urlRegex: '/admin-website-content',
        requires: {
          usertypes: ['agency-owner', 'system-admin', 'internal-admin', 'internal-staff'],
        },
      },
      {
        label: 'Blog Posts',
        url: '/admin-blog-posts',
        urlRegex: '/admin-blog-posts',
        requires: {
          usertypes: ['agency-owner', 'system-admin', 'internal-admin', 'internal-staff'],
        },
      },
      {
        label: 'Carrier Contests',
        url: '/admin-carrier-contests',
        urlRegex: '/admin-carrier-contests',
        requires: {
          usertypes: ['agency-owner', 'system-admin', 'internal-admin', 'internal-staff'],
        },
      },
      {
        label: 'Coverages',
        url: '/admin-coverage',
        urlRegex: '/admin-coverage',
        requires: {
          usertypes: ['agency-owner', 'system-admin', 'internal-admin', 'internal-staff'],
        },
      },
      {
        label: 'Create User',
        url: '/admin-create-user',
        urlRegex: '/admin-create-user',
        requires: {
          usertypes: ['agency-owner', 'system-admin', 'internal-admin', 'internal-staff'],
        },
      },
      {
        label: 'Divisions',
        url: '/admin-divisions',
        urlRegex: '/admin-divisions',
        requires: {
          usertypes: ['agency-owner', 'system-admin', 'internal-admin', 'internal-staff'],
        },
      },
      {
        label: 'E-mail Notifications',
        url: '/admin-emails',
        urlRegex: '/admin-emails',
        requires: {
          usertypes: ['agency-owner', 'system-admin', 'internal-admin', 'internal-staff'],
        },
      },
      {
        label: 'E&O Policies',
        url: '/admin-eo-policy',
        urlRegex: '/admin-eo-policy',
        requires: {
          usertypes: ['agency-owner', 'system-admin', 'internal-admin', 'internal-staff'],
        },
      },
      {
        label: 'Internal Resources',
        url: '/internal-resources',
        urlRegex: '/internal-resources',
        requires: {
          usertypes: ['agency-owner', 'system-admin', 'internal-admin', 'internal-staff'],
        },
      },
      {
        label: 'Knowledgebase',
        url: '/kb-support',
        urlRegex: '/kb-support',
        requires: {
          usertypes: ['agency-owner', 'system-admin', 'internal-admin', 'internal-staff'],
        },
      },
      {
        label: 'Lead Programs',
        url: '/admin-lead-programs',
        urlRegex: '/admin-lead-programs',
        requires: {
          usertypes: ['agency-owner', 'system-admin', 'internal-admin', 'internal-staff'],
        },
      },
      {
        label: 'Lead Sources',
        url: '/admin-leads',
        urlRegex: '/admin-leads',
        requires: {
          usertypes: ['agency-owner', 'system-admin', 'internal-admin', 'internal-staff'],
        },
      },
      {
        label: 'Manage Messages',
        url: '/manage-messaging',
        urlRegex: '/manage-messaging',
        requires: {
          usertypes: ['agency-owner', 'system-admin', 'internal-admin', 'internal-staff'],
        },
      },
      {
        label: 'Package & Features',
        url: '/admin-packages',
        urlRegex: '/admin-packages',
        requires: {
          usertypes: ['agency-owner', 'system-admin', 'internal-admin', 'internal-staff'],
        },
      },
      {
        label: 'Products',
        url: '/admin-products',
        urlRegex: '/admin-products',
        requires: {
          usertypes: ['agency-owner', 'system-admin', 'internal-admin', 'internal-staff'],
        },
      },
      {
        label: 'Promotion Codes',
        url: '/admin/promotion-codes',
        urlRegex: '/admin/promotion-codes',
        requires: {
          usertypes: ['agency-owner', 'system-admin', 'internal-admin', 'internal-staff'],
        },
      },
      {
        label: 'Prospects',
        url: '/admin-prospects',
        urlRegex: '/admin-prospects',
        requires: {
          usertypes: ['agency-owner', 'system-admin', 'internal-admin', 'internal-staff'],
        },
      },
      {
        label: 'Referrals',
        url: '/admin-referrals',
        urlRegex: '/admin-referrals',
        requires: {
          usertypes: ['agency-owner', 'system-admin', 'internal-admin', 'internal-staff'],
        },
      },
      {
        label: 'Royal Ring Recipients',
        url: '/admin-royal-rings',
        urlRegex: '/admin-royal-rings',
        requires: {
          usertypes: ['agency-owner', 'system-admin', 'internal-admin', 'internal-staff'],
        },
      },
      {
        label: 'Reports',
        url: '/admin-reports',
        urlRegex: '/admin-reports',
        requires: {
          usertypes: ['agency-owner', 'system-admin', 'internal-admin', 'internal-staff'],
        },
      },
      {
        label: 'Resources',
        url: '/admin-resources',
        urlRegex: '/admin-resources',
        requires: {
          usertypes: ['agency-owner', 'system-admin', 'internal-admin', 'internal-staff'],
        },
      },
      {
        label: 'Resource Types',
        url: '/admin-resourceType',
        urlRegex: '/admin-resourceType',
        requires: {
          usertypes: ['agency-owner', 'system-admin', 'internal-admin', 'internal-staff'],
        },
      },
      {
        label: 'Terms',
        url: '/admin-terms',
        urlRegex: '/admin-terms',
        requires: {
          usertypes: ['agency-owner', 'system-admin', 'internal-admin', 'internal-staff'],
        },
      },
      {
        label: 'Tools',
        url: '/admin-techtools',
        urlRegex: '/admin-techtools',
        requires: {
          usertypes: ['agency-owner', 'system-admin', 'internal-admin', 'internal-staff'],
        },
      },
      {
        label: 'Training Quizzes',
        url: '/admin/training-quizes',
        urlRegex: '/admin/training-quizes',
        requires: {
          usertypes: ['agency-owner', 'system-admin', 'internal-admin', 'internal-staff'],
        },
      },
      {
        label: 'Quote Requests',
        url: '/quote-requests',
        urlRegex: '/quote-requests',
        requires: {
          usertypes: ['agency-owner', 'system-admin', 'internal-admin', 'internal-staff'],
        },
      },
      {
        label: 'Update Points',
        url: '/admin-points',
        urlRegex: '/admin-points',
        requires: {
          usertypes: ['agency-owner', 'system-admin', 'internal-admin', 'internal-staff'],
        },
      },
      {
        label: 'Backoffice User Manuals',
        url: '/user-manuals',
        urlRegex: '/user-manuals',
        requires: {
          usertypes: ['agency-owner', 'system-admin', 'internal-admin', 'internal-staff'],
        },
      },
      {
        label: 'Technical Documentation',
        url: '/tech-manuals',
        urlRegex: '/tech-manuals',
        requires: {
          usertypes: ['system-admin'],
        },
      },
      {
        label: 'Virtual Vault',
        url: '/virtual-vault',
        urlRegex: '/virtual-vault',
        requires: {
          usertypes: ['agency-owner', 'system-admin', 'internal-admin', 'internal-staff'],
        },
      },
    ],
  },
}

export { MAJOR_NAV_LINKS, MINOR_NAV_LINKS }
